import React, { FC } from 'react';
import PrinterStatus from '~Components/PrinterStatus';
import SystemStatus from '~Components/SystemStatus';
import { useStorage } from '~Components/Storage';
import IncomingOrdersIndicator from '~Scenes/IncomingAppOrders/IncomingOrdersIndicator';
import * as actions from '~actions';
import PrepTimeControls from '../HeaderBar/PrepTimeControls';
import StatusColorInfo from '../StatusColorInfo';
import styled from '@emotion/styled';
import UndoIcon from '~Assets/icons/undo.svg';
import {
  useBridgeApi,
  useButton,
  useDispatch,
  useLicenses,
  useLogger,
  useSelector,
  useStaffCan,
} from '~Hooks';
import { ORDER_STATUS } from '@baemingo/order-status-events/dist/types';
import { useHistory } from 'react-router';
import { useOrderStatusApi, useOrderStatusUniverse } from '~Components/OrderStatusUniverse';
import useI18n from '~Translations';
import { Box, TransparentButton } from '~Styles';
import Icon from '~ComponentsImproved/Icon';
import useConfirm from '~Hooks/useConfirm';
import ClearOrders from './ClearOrders';

const Wrapper = styled(Box)`
  background-color: var(--colorBlueGray1300);
  height: 64px;
  z-index: 1;
  box-shadow: 0px 0px 64px 0px rgba(0, 0, 0, 0.64);
`;

const MenuButtonCol = styled(Box)`
  width: 400px;
`;

const MenuButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 0 16px;
  height: 100%;
`;

const ActionButtonsWrapper = styled(Box)`
  text-align: center;
  width: 400px;
`;

const ActionButton = styled(TransparentButton)`
  white-space: nowrap;
`;

const StyledIcon = styled.i`
  color: var(--colorBlueGray500);
  font-size: 18px;
  margin: 0 16px 0 0;
  height: 24px;
  width: 24px;
  text-align: center;
  line-height: 23px;
`;

const SelectedStation = styled.div`
  display: flex;
  height: 100%;
  color: var(--colorBlueGray800);
  align-items: center;
  margin-left: 32px;
`;

const PrepTimeWrapper = styled.div`
  display: flex;
  height: 64px;
  align-items: center;
`;

interface Props {
  venueId: string;
  isModal?: boolean;
}

const KdsBottomBar: FC<Props> = ({ venueId, isModal }) => {
  const { i18n } = useI18n();
  const [storage] = useStorage();
  const licenses = useLicenses();
  const staffCan = useStaffCan();
  const dispatch = useDispatch();
  const history = useHistory();
  const bridge = useBridgeApi();
  const api = useOrderStatusApi(venueId);
  const universe = useOrderStatusUniverse();

  const selectedKDSStation = useSelector((state) => state.stations)?.find(
    (s) => s.id === storage.selectedKDSStation,
  );

  const shouldShowIncomingAppOrders =
    storage.showIncomingAppOrders ||
    selectedKDSStation?.orderStatuses.includes(ORDER_STATUS.PENDING);

  const isUsingBrowser = !window.ReactNativeWebView;

  const reloadButton = useButton({
    onClick: () => {
      bridge.reload();
    },
  });

  const undoButton = useButton({
    onClick: () => {
      if (!universe.hasHistory) {
        return;
      }

      api.undo();
    },
    disabled: !universe.hasHistory,
  });

  return (
    <Wrapper display="flex" justifyContent="space-between">
      <MenuButtonCol display="flex">
        {!isModal && (
          <MenuButtonWrapper
            onClick={() => {
              staffCan.openMainMenu
                ? dispatch(actions.toggleMainMenu(true))
                : history.push('gateway');
            }}>
            <StyledIcon className="icon hamburger_wide" />
          </MenuButtonWrapper>
        )}

        <PrinterStatus hideBorder={true} shrink={isModal} />

        {!isModal && <SystemStatus hideBorder={true} blinking={true} />}

        {licenses.hasApp && shouldShowIncomingAppOrders && <IncomingOrdersIndicator />}

        {selectedKDSStation?.name && (
          <SelectedStation>
            {i18n('KDS.Station')}: {selectedKDSStation.name}
          </SelectedStation>
        )}
      </MenuButtonCol>

      {storage.KDSLite && (
        <PrepTimeWrapper>
          <PrepTimeControls />
        </PrepTimeWrapper>
      )}

      {!storage.KDSLite && <StatusColorInfo />}

      <ActionButtonsWrapper display="flex" justifyContent="flex-end" gap="2xl">
        {isUsingBrowser && <ClearOrders venueId={venueId} />}

        {!isModal && <ActionButton {...reloadButton}>{i18n('KDS.Reload')}</ActionButton>}

        <TransparentButton
          {...undoButton}
          display="flex"
          alignItems="center"
          gap="xs"
          marginRight="small">
          <Icon style={{ flexShrink: 0 }} src={UndoIcon} color="var(--colorBlueGray200)" />
          {i18n('KDS.Undo')}
        </TransparentButton>
      </ActionButtonsWrapper>
    </Wrapper>
  );
};

export default React.memo(KdsBottomBar);
